import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withXhr } from '@angular/common/http';
import localeRo from '@angular/common/locales/ro';
import { ApplicationConfig, LOCALE_ID } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    PreloadAllModules,
    provideRouter,
    withInMemoryScrolling,
    withPreloading,
} from '@angular/router';
import { provideFuse } from '@fuse';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { mockApiServices } from 'app/mock-api';
import { KATEX_OPTIONS, MARKED_EXTENSIONS, provideMarkdown, SANITIZE } from 'ngx-markdown';
import { legacyMathEscapesExtension } from 'app/core/utils/legacy-math-escapes.utils';
import { SecurityContext } from '@angular/core';
registerLocaleData(localeRo);

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(withXhr()),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),
        { provide: LOCALE_ID, useValue: 'ro' },
        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },
        // KaTeX output must skip the Angular sanitizer (it strips the inline
        // `top:` styles that position sub/superscripts) — staff-authored content.
        provideMarkdown({
            sanitize: { provide: SANITIZE, useValue: SecurityContext.NONE },
            // nonStandard: exercises write "$…$;" which the default rule rejects
            katexOptions: {
                provide: KATEX_OPTIONS,
                useValue: { throwOnError: false, nonStandard: true },
            },
            // Exercises were authored against the pre-22 pipeline, which escaped
            // the math before KaTeX saw it — so their backslashes come doubled.
            markedExtensions: [
                {
                    provide: MARKED_EXTENSIONS,
                    useValue: legacyMathEscapesExtension,
                    multi: true,
                },
            ],
        }),

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                services: mockApiServices,
            },
            fuse: {
                layout: 'classy',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-default',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
    ],
};
